<template>
  <div class="l-shell">
    <app-sidebar
      :items="sidebarItems"
      :collapsed="isMobile ? !mobileNavOpen : sidebarCollapsed"
      :mobile-open="mobileNavOpen"
      @home="goToHome"
      @toggle-collapse="toggleSidebar"
    />

    <transition name="l-shell-fade">
      <div
        v-if="mobileNavOpen"
        class="l-shell__overlay"
        @click="mobileNavOpen = false"
      />
    </transition>

    <div class="l-shell__main">
      <app-topbar
        :current="currentNavItem"
        :user="user"
        :is-admin="!!isAdmin()"
        :night-mode="isNightMode"
        @toggle-theme="handleThemeToggle(!isNightMode)"
        @change-password="changePassword"
        @logout="logout"
      />

      <main class="l-home">
        <router-view />
        <snack-bar />
      </main>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SnackBar from '../../shared/snackebar/SnackeBar';
import { authService } from '../../services/auth';
import ChangePasswordModal from '../change-password';
import AppSidebar from '../../shared/app-shell/AppSidebar';
import AppTopbar from '../../shared/app-shell/AppTopbar';
import { isNavItemActive } from '../../shared/app-shell/nav';
import {defaultOnLineSidebarItems} from "@/const/on-line-sidebar-items";
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

function readSidebarCollapsed() {
  try {
    return JSON.parse(localStorage.getItem(SIDEBAR_COLLAPSED_KEY)) === true;
  } catch (e) {
    return false;
  }
}

export default {
  components: {
    SnackBar,
    AppSidebar,
    AppTopbar,
  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      sidebarItems: [],
      sidebarCollapsed: readSidebarCollapsed(),
      mobileNavOpen: false,
      isMobile: false,
      mobileQuery: null,
    };
  },

  computed: {
    ...mapGetters('account', ['user']),
    ...mapGetters('theme', ['isNightMode']),

    currentNavItem() {
      for (const item of this.sidebarItems) {
        const activeSubItem = (item.subItems || []).find(subItem => isNavItemActive(this.$route, subItem));

        if (activeSubItem) {
          return { ...activeSubItem, parent: item.text };
        }

        if (isNavItemActive(this.$route, item)) {
          return item;
        }
      }

      return null;
    },
  },

  watch: {
    $route() {
      this.mobileNavOpen = false;
    },

    // the menu was built once in created(): if permissions arrive (or change) later it stayed empty
    userOnlinePermissions() {
      this.sidebarItems = this.filterSidebarItems();
    },
  },

  mounted() {
    this.mobileQuery = window.matchMedia('(max-width: 960px)');
    this.updateIsMobile();
    this.mobileQuery.addListener(this.updateIsMobile);
  },

  beforeDestroy() {
    this.mobileQuery.removeListener(this.updateIsMobile);
  },

  created() {
    this.sidebarItems = this.filterSidebarItems();

    if (this.$router.currentRoute.name === 'home' && !this.isAdmin()) {
      const firstAllowedRoutePathName = this.getFirstAllowedRoutePathName();
      this.$router.push({name: firstAllowedRoutePathName});
    }

    if (this.$router.currentRoute.name === 'home' && this.isAdmin()) {
      this.$router.push({name: 'websites'});
    }
  },

  methods: {
    ...mapActions('theme', ['handleThemeToggle']),

    updateIsMobile() {
      this.isMobile = this.mobileQuery.matches;

      if (!this.isMobile) {
        this.mobileNavOpen = false;
      }
    },

    toggleSidebar() {
      if (this.isMobile) {
        this.mobileNavOpen = !this.mobileNavOpen;
        return;
      }

      this.sidebarCollapsed = !this.sidebarCollapsed;

      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(this.sidebarCollapsed));
      } catch (e) {
        // storage unavailable: keep the in-memory state only
      }
    },

    logout() {
      authService.logout(this.user.email);
    },

    changePassword() {
      this.$modal.show(
        ChangePasswordModal,
        {},
        { height: 'auto', scrollable: true }
      );
    },

    goToHome() {
      const firstAllowedRoutePathName = this.getFirstAllowedRoutePathName();

      // logo click on the page you're already on: vue-router rejects with NavigationDuplicated, which is harmless
      const ignoreDuplicate = error => {
        if (!error || error.name !== 'NavigationDuplicated') {
          throw error;
        }
      };

      if (firstAllowedRoutePathName && this.$router.currentRoute.name !== firstAllowedRoutePathName && !this.isAdmin()) {
        this.$router.push({name: firstAllowedRoutePathName}).catch(ignoreDuplicate);
      }

      if (this.$router.currentRoute.name !== 'websites' && this.isAdmin()) {
        this.$router.push({name: 'websites'}).catch(ignoreDuplicate);
      }
    },

    filterSidebarItems() {
      return defaultOnLineSidebarItems.filter(sidebarItem => {
        if (sidebarItem.onlyAdmins) {
          return this.isAdmin();
        }

        if (sidebarItem.onlyDevelopers) {
          return this.isDeveloper();
        }

        if (sidebarItem.requiredPermissions) {
          return sidebarItem.requiredPermissions.some(permission => this.canRead(permission))
        }

        return true;
      })
    },

  },

};
</script>

<style lang="scss">
.l-shell {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: var(--bg-app);
}

.l-shell__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

// the topbar is 64px tall; inner pages size themselves against that
.l-home {
  position: relative;
  height: calc(100vh - 64px);
  overflow: hidden;
  padding: 0;
  background-color: var(--bg-app);

  // kept from vue-material's md-app-content, which pages were built against
  .md-card {
    margin-right: 16px;
    margin-left: 16px;
    overflow: visible;
  }
}

.l-shell__overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(10, 11, 13, 0.5);
  backdrop-filter: blur(2px);
  z-index: $zIndexDialog - 1;
}

.l-shell-fade-enter-active,
.l-shell-fade-leave-active {
  transition: opacity var(--duration-base) ease;
}

.l-shell-fade-enter,
.l-shell-fade-leave-to {
  opacity: 0;
}
</style>
